import { Tag } from "antd";
import { useGetRequestsQuery, useUpdateRequestMutation } from "api/TenantSlug/Request/hooks";
import { useCommissionGroupSelectOptions } from "components/UI/Selects/CommissionGroupSelectSearchable/useCommissionGroupSelectOptions";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import dayjs from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useRoleLabel } from "hooks/useRoleLabel";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { UserRequestStatus } from "types/enums";

import { ApproveRejectButtons } from "../shared/ActionChips";
import { useOptimisticHide } from "../shared/hooks/useOptimisticHide";
import { SearchableTable } from "../shared/SearchableTable";
import { ApiErr, UpdateRequestVariables } from "../shared/types";
import { renderDateSmart, syncDayjsLocale } from "../shared/utils/dateLocale";

import UserRequestData = App.Data.UserRequestData;

export const WorkspaceRequests: React.FC = () => {
    const { t, i18n } = usePaldockTranslation("ui.dashboard.cards.workspace-requests");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const roleLabel = useRoleLabel();

    useEffect(() => {
        syncDayjsLocale(i18n.language);
    }, [i18n.language]);

    const query = useGetRequestsQuery(
        {
            page: {
                number: 1,
                size: 5,
            },
            filter: {
                filter: { type: { in: "tenant,tenant-invite" }, status: "pending" },
                sort: "-created_at",
                include: "user",
            },
        },
        {
            staleTime: 5000,
        }
    );

    const { options: commissionGroupOptions } = useCommissionGroupSelectOptions();

    const [groupByReq, setGroupByReq] = useState<Record<string, string | undefined>>({});

    const { hide, unhide, filterVisible } = useOptimisticHide();

    const updateRequestMutation = useUpdateRequestMutation({
        onSuccess: async () => {
            await query.refetch();
            toast.success(t("messages.update_success"));
        },
        onError: (_err: ApiErr, variables: UpdateRequestVariables) => {
            unhide(variables.requestId);
            toast.error(t("messages.update_error"));
        },
    });

    const updateStatus = (r: UserRequestData, status: UserRequestStatus) => {
        const commission_group_id = groupByReq[r.id] ?? r?.data?.group ?? undefined;
        hide(r.id);
        updateRequestMutation.mutate({
            requestId: r.id,
            request: { status, commission_group_id },
        });
    };

    const rows = useMemo(
        () => filterVisible<UserRequestData>(query.data?.user_requests, "pending"),
        [query.data?.user_requests, filterVisible]
    );

    return (
        <SearchableTable<UserRequestData>
            rows={rows}
            loading={query.isLoading || query.isRefetching}
            rowKey={(r) => r.id}
            searchKeys={[
                (r) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY HH:mm") : ""),
                (r) => (r.created_at ? dayjs(r.created_at).format("D.M.YYYY") : ""),
                (r) => r?.user?.nickname ?? "",
                (r) => roleLabel(r?.data?.roles?.[0]) ?? "",
                (r) => {
                    const gid = groupByReq[r.id] ?? r?.data?.group;
                    const g = commissionGroupOptions.find((x) => x.value === gid);
                    return g?.label ?? "";
                },
            ]}
            allowSearch={false}
            columns={[
                {
                    title: tG("date"),
                    dataIndex: "created_at",
                    render: (v: string) => (
                        <span title={v ? dayjs(v).format("D.M.YYYY HH:mm") : ""}>{renderDateSmart(v)}</span>
                    ),
                },
                {
                    title: tG("user"),
                    dataIndex: ["user", "nickname"],
                    render: (_: unknown, r: UserRequestData) => <EntityLinkCell.Owner entity={r.user} mode="drawer" />,
                },
                {
                    title: tG("type"),
                    dataIndex: "type",
                    render: (_: unknown, r: UserRequestData) => (
                        <div className="flex items-center gap-2">
                            <span>{roleLabel(r?.data?.roles?.[0])}</span>
                            <Tag color={(r.type as string) === "tenant-invite" ? "geekblue" : "default"}>
                                {(r.type as string) === "tenant-invite" ? t("type.invitation") : t("type.request")}
                            </Tag>
                        </div>
                    ),
                },
                {
                    title: tG("group"),
                    key: "group",
                    width: 200,
                    render: (_: unknown, r: UserRequestData) => {
                        const value = groupByReq[r.id] ?? r?.data?.group ?? "";
                        return (
                            <select
                                value={value}
                                onChange={(e) =>
                                    setGroupByReq((prev) => ({ ...prev, [r.id]: e.target.value || undefined }))
                                }
                                style={{ width: "100%" }}
                            >
                                <option value="">{t("options.none")}</option>
                                {commissionGroupOptions?.map((g) => (
                                    <option key={String(g.value)} value={String(g.value)}>
                                        {g.label}
                                    </option>
                                ))}
                            </select>
                        );
                    },
                },
                {
                    title: tG("actions"),
                    key: "actions",
                    align: "center",
                    width: 140,
                    render: (_: unknown, r: UserRequestData) => (
                        <ApproveRejectButtons
                            state={r.status}
                            pendingValue="pending"
                            approvedValue="accepted"
                            rejectedValue="rejected"
                            labels={{
                                approve: tC("accept"),
                                reject: tC("reject"),
                            }}
                            hideApprove={(r.type as string) === "tenant-invite"}
                            loading={updateRequestMutation.isPending}
                            onApprove={() => updateStatus(r, UserRequestStatus.Accepted)}
                            onReject={() => updateStatus(r, UserRequestStatus.Rejected)}
                        />
                    ),
                },
            ]}
            maxRows={5}
            compact
        />
    );
};
